import { gql, useMutation } from "@apollo/client";
import { useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";

import useAuth from "@/hooks/use-auth";
import useTrackCartEvent from "@/pages/cart/hooks/use-track-cart-event";

const ABANDON_CART_MUTATION = gql`
  mutation AbandonCart($id: String) {
    abandonCart(id: $id) {
      success
    }
  }
`;

interface DataType {
  abandonCart: {
    success: boolean;
  };
}

interface VariablesType {
  id: string;
}

function useAbandonCart() {
  return useMutation<DataType, VariablesType>(ABANDON_CART_MUTATION);
}

export default function AbandonCart() {
  const [abandonCart] = useAbandonCart();
  const track = useTrackCartEvent();
  const { isUser, mutationContext } = useAuth();
  const asPath = useRouterState({ select: (s) => s.location.href });

  // biome-ignore lint/correctness/useExhaustiveDependencies: abandonCart shouldnt change
  useEffect(() => {
    if (!isUser) {
      return;
    }

    const processKeys = async () => {
      for (const key of Object.keys(sessionStorage)) {
        if (!key.startsWith("cart/")) {
          continue;
        }
        if (asPath.slice(1).startsWith(key)) {
          continue;
        }

        try {
          await abandonCart(mutationContext({ id: key.slice(5) }));
        } catch (_e) {
          // Silently fail - error already logged to Sentry via apollo error link
        }

        track(
          "Booking Cart Abandoned",
          JSON.parse(sessionStorage.getItem(key) || "{}")
        );
        sessionStorage.removeItem(key);
      }
    };

    processKeys();
  }, [asPath, isUser]);

  return null;
}
